import { useState } from 'react'
import { router } from '@inertiajs/react'

import { Link } from '@atoms'
import { Progress } from '@atoms/Progress'
import { DropdownMenuItem, DropdownMenuSeparator } from '@components/atoms/DropdownMenu'
import { ConfirmDialog } from '@components/molecules/ConfirmDialog'
import { DotsMenu } from '@components/molecules/DotsMenu'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('views.call_lists.index')
const tButtons = scopedTranslation('shared.buttons')

export type CallListActionsProps = {
  callList: {
    name: string
    sublistsCount: number
    callable: boolean
    href: string
    editHref: string
    addSupportersHref: string
    smsBlastHref: string
    newCallingSessionHref: string
    activeCallingSession: { href: string; completedCount: number; totalCount: number } | null
  }
  canCall: boolean
  canSendSmsBlasts: boolean
  onRemove: (href: string) => void
  onRemoveFinished: (href: string) => void
}

export function CallListActions({
  callList,
  canCall,
  canSendSmsBlasts,
  onRemove,
  onRemoveFinished,
}: CallListActionsProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const session = canCall ? callList.activeCallingSession : null

  const deleteCallList = () => {
    onRemove(callList.href)
    router.delete(callList.href, { preserveScroll: true, onFinish: () => onRemoveFinished(callList.href) })
  }

  return (
    <div className="tw:inline-flex tw:items-start tw:gap-2 tw:text-left">
      {session && (
        <div className="tw:flex tw:flex-col tw:gap-1">
          <Link as="inertiaLink" href={session.href} size="sm" rank="secondary">
            {tButtons('continue_calling_session')}
          </Link>
          <Progress
            size="compact"
            value={session.totalCount ? Math.round((session.completedCount / session.totalCount) * 100) : 0}
            aria-label={t('calling_progress_label', { name: callList.name })}
          />
          <span className="tw:text-right tw:text-xs tw:tabular-nums tw:text-gray-500">
            {t('calling_progress', { completed: session.completedCount, total: session.totalCount })}
          </span>
        </div>
      )}
      {canCall && !session && callList.callable && (
        <Link href={callList.newCallingSessionHref} size="sm" data-modal="true">
          {tButtons('create_calling_session')}
        </Link>
      )}
      <DotsMenu ariaLabel={t('actions.menu_label', { name: callList.name })}>
        <DropdownMenuItem asChild>
          <a href={callList.addSupportersHref} data-modal="true">
            <Icon type="user-plus-01" />
            {t('add_supporters')}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={callList.editHref} data-modal="true">
            <Icon type="pencil-01" />
            {tButtons('edit')}
          </a>
        </DropdownMenuItem>
        {canSendSmsBlasts && (
          <DropdownMenuItem asChild>
            <a href={callList.smsBlastHref} data-modal="true">
              <Icon type="message-chat-square" />
              {t('actions.send_sms_blast')}
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => setConfirmingDelete(true)}>
          <Icon type="trash-01" />
          {t('actions.delete')}
        </DropdownMenuItem>
      </DotsMenu>
      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title={t('delete_dialog.title', { name: callList.name })}
        body={
          callList.sublistsCount > 0
            ? t('delete_dialog.body_with_sublists', { count: callList.sublistsCount })
            : t('delete_dialog.body')
        }
        confirmLabel={t('actions.delete')}
        confirmIcon="trash-01"
        onConfirm={deleteCallList}
      />
    </div>
  )
}
